import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  type DisplayMessage,
  MessageStatus,
  MessageType,
} from "@/api/ai/types";
import { useAiChatContext } from "@/components/ai/chat/AiChatContext";
import type {
  StudioActionResult,
  StudioActionRow,
  StudioActionsHost,
} from "@/components/ai/chat/StudioActionsContext";
import {
  extractStudioFences,
  groupOf,
  parseStudioActions,
  type StudioAction,
} from "@/components/ai/chat/studioActionFence";
import { type ResolvedAction, resolveStudioAction } from "./studioActions";
import { useApplyEffect } from "./useStudioEffects";
import { useStudioState } from "./useStudioState";

// The Studio side of the actions channel: applies a streamed message's
// auto actions once when it completes, runs click actions on demand, and
// remembers what each action did so the chat row can show it (the
// appliers themselves live in useStudioEffects). Messages loaded from
// history were never seen streaming here, so nothing from an old
// conversation replays.

type Results = Record<string, StudioActionResult>;

function key(messageId: string, code: string, index: number): string {
  return `${messageId} ${index} ${code}`;
}

function rowOf(resolved: ResolvedAction, index: number): StudioActionRow {
  const base = {
    index,
    group: resolved.group,
    label: resolved.label,
    button: resolved.button,
  };
  return resolved.ok
    ? { ...base, enabled: true }
    : { ...base, enabled: false, reason: resolved.reason };
}

type RunAction = (
  messageId: string,
  code: string,
  index: number,
  action: StudioAction,
) => void;

function isStreaming(m: DisplayMessage): boolean {
  return (
    m.status === MessageStatus.Pending || m.status === MessageStatus.Streaming
  );
}

// The auto actions in a message's json:studio fences, with the fence and
// position each came from (the chat row's key).
function autoActionsOf(markdown: string) {
  return extractStudioFences(markdown).flatMap((code) =>
    parseStudioActions(code)
      .actions.map((action, index) => ({ code, index, action }))
      .filter(({ action }) => groupOf(action) === "auto"),
  );
}

// The auto actions of every assistant message that completed in this
// session, applied once each.
function useAutoApply(runAction: RunAction) {
  const { messages } = useAiChatContext();
  const seenStreaming = useRef(new Set<string>());
  const applied = useRef(new Set<string>());

  useEffect(() => {
    const seen = seenStreaming.current;
    const done = applied.current;
    for (const m of messages) {
      if (m.type !== MessageType.Assistant) continue;
      if (isStreaming(m)) {
        seen.add(m.messageId);
        continue;
      }
      const completedNow =
        m.status === MessageStatus.Complete &&
        seen.has(m.messageId) &&
        !done.has(m.messageId);
      if (!completedNow) continue;
      done.add(m.messageId);
      for (const { code, index, action } of autoActionsOf(m.message)) {
        runAction(m.messageId, code, index, action);
      }
    }
  }, [messages, runAction]);
}

export function useStudioActions(): StudioActionsHost {
  const state = useStudioState();
  const apply = useApplyEffect();
  const [results, setResults] = useState<Results>({});

  const record = useCallback(
    (k: string, result: StudioActionResult) =>
      setResults((prev) => ({ ...prev, [k]: result })),
    [],
  );

  // Resolve + apply one action now, against the live state. A run's
  // verdict arrives later; the row shows it in flight meanwhile.
  const runAction = useCallback(
    (messageId: string, code: string, index: number, action: StudioAction) => {
      const k = key(messageId, code, index);
      const resolved = resolveStudioAction(action, state);
      if (!resolved.ok) return record(k, { ok: false, text: resolved.reason });
      const applied = apply(resolved.effect);
      if (!(applied instanceof Promise)) return record(k, applied);
      record(k, { ok: true, text: "running…", pending: true });
      void applied.then((result) => record(k, result));
    },
    [state, apply, record],
  );

  useAutoApply(runAction);

  return useMemo<StudioActionsHost>(
    () => ({
      describe: (code) => {
        const { actions, errors } = parseStudioActions(code);
        const rows = actions.map((a, i) =>
          rowOf(resolveStudioAction(a, state), i),
        );
        return { rows, errors };
      },
      run: (messageId, code, index) => {
        const action = parseStudioActions(code).actions[index];
        if (action && groupOf(action) === "click") {
          runAction(messageId, code, index, action);
        }
      },
      resultFor: (messageId, code, index) =>
        results[key(messageId, code, index)],
    }),
    [state, results, runAction],
  );
}
